import {
  Text,
  Button,
  Link,
  Badge,
  useDisclosure,
  Box,
  Textarea,
  Avatar,
  Flex,
  Spacer,
  Alert,
  AlertIcon,
  Grid
} from '@chakra-ui/react'
import React, { Fragment, ReactNode, useEffect } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { FormModal } from '../../../components/FormModal'
import { FormSelect } from '../../../components/Select'
import { formatApiDate, formatDateTime, useAppToast, useHasRole, formatPhone } from '../../../helpers'
import { RequestNoteService } from '../../../services/Request/RequestNoteService'
import { RequestService } from '../../../services/Request/RequestService'
import { statuses } from './RequestConsts'
import { RequestDeleteDialog } from './RequestDeleteDialog'
import { RequestNoteDeleteDialog } from './RequestNoteDeleteDialog'
import { RequestStatusLog } from './RequestStatusLog'

interface IProps {
  isOpen: boolean
  onClose: () => void
  refreshRequest: (requestId: number) => Promise<void>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: any
}

interface IRequestField {
  label: ReactNode
  value?: ReactNode
}

/**
 * Ficha de campos própria desta tela, fora do `<DetailSheet>` padrão: pedido explícito de
 * legibilidade para esta tela (rótulo e valor colados em duas colunas à esquerda, fontes maiores) —
 * não é para copiar em outra tela sem o mesmo pedido, veja `CLAUDE.md` > "Detail sheets".
 */
function RequestFieldsGrid({ fields }: { fields: (IRequestField | false | null | undefined | '')[] }) {
  const visible = fields.filter((field): field is IRequestField => Boolean(field))

  return (
    <Grid templateColumns="minmax(200px, max-content) 1fr" columnGap={6} rowGap={3} px={5}>
      {visible.map((field, index) => {
        const isEmpty = field.value == null || field.value === ''
        const isLast = index === visible.length - 1

        return (
          <Fragment key={index}>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="gray.600"
              pb={3}
              borderBottom={isLast ? 'none' : '1px solid'}
              borderColor="gray.100"
            >
              {field.label}
            </Text>
            <Text
              fontSize="md"
              color={isEmpty ? 'gray.400' : 'gray.800'}
              wordBreak="break-word"
              pb={3}
              borderBottom={isLast ? 'none' : '1px solid'}
              borderColor="gray.100"
            >
              {isEmpty ? '-' : field.value}
            </Text>
          </Fragment>
        )
      })}
    </Grid>
  )
}

const ignoredKeys = [
  'bairro',
  'carteira_estudante',
  'cep',
  'cidade',
  'complemento',
  'documento_com_foto',
  'endereco_completo',
  'estado',
  'numero',
  'phone_confirm',
  'reposicao_justificada',
  'schamada_justificada',
  'tipo_asunto_financeiro',
  'your_email',
  'your_email_confirm'
]

export function RequestViewModal({ isOpen, onClose, request, refreshRequest }: IProps) {
  const requestNoteService = new RequestNoteService()
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const {
    isOpen: deleteNoteDialogIsOpen,
    onOpen: deleteNoteDialogOnOpen,
    onClose: deleteNoteDialogIsClose
  } = useDisclosure()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [notes, setNotes] = React.useState<any>([])
  const [note, setNote] = React.useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [user, setUser] = React.useState<any>({})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [noteToDelete, setNoteToDelete] = React.useState<any>({})
  const [editingConsultant, setEditingConsultant] = React.useState(false)
  const [consultants, setConsultants] = React.useState<string[]>([])
  const [consultant, setConsultant] = React.useState<string>('')
  const hasRole = useHasRole()
  const permissions = {
    editRequests: hasRole('edit_requests'),
    deleteRequests: hasRole('delete_requests')
  }

  const requestService = new RequestService()
  const toast = useAppToast()

  useEffect(() => {
    loadNotes()
    setEditingConsultant(false)
  }, [request])

  useEffect(() => {
    const user = localStorage.getItem('user')

    if (user) {
      setUser(JSON.parse(user))
    }

    requestService.getRequestsConsultants().then((response: { consultants: string[] }) => {
      setConsultants(response.consultants)
    })
  }, [])

  const loadNotes = async () => {
    if (!request.id) return

    requestNoteService.list(request.id).then((response) => {
      setNotes(response.notes)
    })
  }

  const handleDelete = async () => {
    deleteDialogOnOpen()
  }

  const handleStatus = async (status: string) => {
    try {
      await requestService.setNewStatus(request.id, status)
      await refreshRequest(request.id)

      toast.success('Solicitação atualizada com sucesso')
    } catch (error) {
      toast.error('Erro ao atualizar solicitação', error)
    }
  }

  const handleSaveNote = async () => {
    if (note === '') return

    try {
      setNote('')
      await requestNoteService.create(request.id, note)
      await loadNotes()
    } catch (error) {
      toast.error('Erro ao salvar anotação', error)
    }
  }

  const formatField = (value: string | null): string | null | ReactNode => {
    if (Array.isArray(value)) {
      return value.map((value, key) => (
        <Box key={key} display="block" my={1}>
          - {formatField(value)}
        </Box>
      ))
    } else if (value === null || value === undefined || value === '') {
      return value
    } else if (
      value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/) ||
      value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}Z$/)
    ) {
      return formatDateTime(value, { showSeconds: true })
    } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatApiDate(value)
    } else if (value.startsWith('https://')) {
      return (
        <Link href={value} isExternal color="blue.500" target="_blank" rel="noreferrer" mr={2}>
          Clique aqui para abrir
        </Link>
      )
    }

    return value
  }

  const formatNameField = (value: string) => {
    return value.replace(/_/g, ' ')
  }

  /** Os três campos cujo valor é uma lista de arquivos enviados: mesmo desenho, mesma linha. */
  const fileListFields = (entries: [string, { link: string; filename: string }[] | undefined][]) =>
    entries
      .filter(([, files]) => files?.length)
      .map(([label, files]) => ({
        label,
        value: (
          <Flex direction="column" align="flex-start" gap={1}>
            {files?.map(({ link, filename }) => (
              <Link href={link} isExternal color="blue.500" rel="noreferrer" key={filename}>
                {filename}
              </Link>
            ))}
          </Flex>
        )
      }))

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDeleteNote = async (note: any) => {
    setNoteToDelete(note)
    deleteNoteDialogOnOpen()
  }

  const onCloseNoteDelete = () => {
    deleteNoteDialogIsClose()
    loadNotes()
  }

  const handleChangeConsultant = () => {
    setEditingConsultant(false)
    requestService.setNewConsultant(request.id, consultant)
    refreshRequest(request.id)
    toast.success('Consultor de sucesso atualizado')
  }

  return (
    request.id && (
      <>
        <FormModal
          isOpen={isOpen}
          onClose={onClose}
          title={
            <>
              Solicitação de: <strong>{request.your_name}</strong>
              <Badge
                variant="solid"
                colorScheme={statuses[request.status].color}
                size="sm"
                ml={2}
                color={statuses[request.status].textColor}
              >
                {statuses[request.status].text}
              </Badge>
            </>
          }
          size="editor"
          footer={
            <>
              {permissions.deleteRequests && (
                <Button colorScheme="red" onClick={handleDelete}>
                  Excluir
                </Button>
              )}
              {permissions.editRequests && (
                <>
                  {(request.status === 'open' || request.status === 'solved') && (
                    <Button
                      colorScheme="abraGray"
                      onClick={() => {
                        handleStatus('in_progress')
                      }}
                      ml={4}
                    >
                      Marcar como em andamento
                    </Button>
                  )}
                  {request.status === 'in_progress' && (
                    <Button
                      colorScheme="abraPurple"
                      onClick={() => {
                        handleStatus('open')
                      }}
                      ml={4}
                    >
                      Marcar como em aberto
                    </Button>
                  )}
                  {request.tipoDeSolicitacao === 'Carteira de Estudante ABRA' && (
                    <Button
                      colorScheme="abraYellow"
                      color="gray.900"
                      onClick={() => {
                        handleStatus('waiting_payment')
                      }}
                      ml={4}
                    >
                      Aguardando Pagamento
                    </Button>
                  )}
                  {request.status === 'in_progress' && (
                    <Button
                      colorScheme="abraGreen"
                      onClick={() => {
                        handleStatus('solved')
                      }}
                      ml={4}
                    >
                      Marcar como resolvido
                    </Button>
                  )}
                </>
              )}
            </>
          }
        >
          {request.transfer_requests_count &&
            (request.transfer_requests_count > 1 ? (
              <Alert status="warning">
                <AlertIcon />
                ATENÇÃO! {request.transfer_requests_count}ª TRANSFERÊNCIA NO MESMO CURSO: NECESSÁRIO ENVIAR LINK DE
                COBRANÇA.
              </Alert>
            ) : (
              <Alert status="success">
                <AlertIcon />
                Essa é a primeira solicitação de mudança de turma do aluno para este curso.
              </Alert>
            ))}
          <Text fontSize="lg" my={4} pl={5} borderBottom="1px solid #eee" pb={2} fontWeight="semibold">
            Dados preenchidos:
          </Text>
          <RequestFieldsGrid
            fields={[
              request.tipoDeSolicitacao && {
                label: 'Tipo de Solicitação',
                value: formatField(request.tipoDeSolicitacao)
              },
              request.data?.tipo_asunto_financeiro && {
                label: 'Tipo de assunto financeiro',
                value: formatField(request.data.tipo_asunto_financeiro)
              },
              { label: 'Data da solicitação', value: formatDateTime(request.created_at) },
              {
                label: 'Consultor de sucesso',
                value: editingConsultant ? (
                  <Flex align="center" justify="flex-start" gap={2}>
                    <FormSelect
                      size="sm"
                      w="220px"
                      value={consultant}
                      onChange={setConsultant}
                      options={[
                        { value: 'Não Informado', label: 'Não Informado' },
                        ...consultants.map((consultant) => ({ value: consultant, label: consultant }))
                      ]}
                      hideEmptyOption
                    />
                    <ActionButton action="save" onClick={handleChangeConsultant} size="sm" />
                    <ActionButton action="cancel" onClick={() => setEditingConsultant(false)} size="sm" />
                  </Flex>
                ) : (
                  <Flex align="center" justify="flex-start" gap={2}>
                    {request.consultor_de_sucesso || 'Não Informado'}
                    {permissions.editRequests && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setConsultant(request.consultor_de_sucesso)
                          setEditingConsultant(true)
                        }}
                        size="sm"
                      />
                    )}
                  </Flex>
                )
              },
              request.responsavel_quem && {
                label: 'Tipo de responsável',
                value: formatField(request.responsavel_quem)
              },
              request.nome_responsavel && {
                label: 'Nome do Responsável',
                value: formatField(request.nome_responsavel)
              },
              request.data?.your_email && {
                label: 'E-mail',
                value: (
                  <Link href={`mailto:${request.data.your_email}`} isExternal color="blue.500" rel="noreferrer">
                    {request.data.your_email}
                  </Link>
                )
              },
              request.phone && {
                label: 'Telefone',
                value: (
                  <Link
                    href={`https://wa.me/55${request.phone.replace(/\D/g, '')}`}
                    isExternal
                    color="blue.500"
                    rel="noreferrer"
                  >
                    {formatPhone(request.phone)}
                  </Link>
                )
              },
              request.formato_curso && { label: 'Formato do Curso', value: formatField(request.formato_curso) },
              request.curso_presencial && { label: 'Curso Presencial', value: formatField(request.curso_presencial) },
              request.curso_online && { label: 'Curso Online', value: formatField(request.curso_online) },
              request.curso_ead_ao_vivo && {
                label: 'Curso EAD Ao Vivo',
                value: formatField(request.curso_ead_ao_vivo)
              },
              request.carga_horaria_curso && {
                label: 'Carga Horária do Curso',
                value: formatField(request.carga_horaria_curso)
              },
              request.data?.endereco_completo && {
                label: 'Endereço completo',
                // A API só grava a linha de `RequestData` quando o campo veio preenchido, então qualquer
                // uma destas chaves pode não existir — interpolar direto imprimiria a palavra `undefined`.
                value:
                  [request.data.endereco_completo, request.data.numero, request.data.complemento, request.data.bairro]
                    .filter(Boolean)
                    .join(', ') +
                  ` - ${request.data.cidade ?? ''}/${request.data.estado ?? ''} - ${request.data.cep ?? ''}`
              },
              ...Object.keys(request.data)
                .filter((key) => ignoredKeys.indexOf(key) === -1)
                .map((key: string) => ({ label: formatNameField(key), value: formatField(request.data[key]) })),
              ...fileListFields([
                ['Carteira Estudante', request.data?.carteira_estudante],
                ['Documento Com Foto', request.data?.documento_com_foto],
                ['Segunda chamada justificada', request.data?.schamada_justificada]
              ]),
              request.data?.reposicao_justificada && {
                label: 'Reposição justificada',
                value: (
                  <Link href={`${request.data.reposicao_justificada}?nocache=1`} isExternal color="blue.500">
                    {request.data.reposicao_justificada}
                  </Link>
                )
              },
              request.updated_at && { label: 'Última atualização', value: formatDateTime(request.updated_at) }
            ]}
          />
          <Text fontSize="lg" mb={4} pl={5} borderBottom="1px solid #eee" pb={2} mt={4} fontWeight="semibold">
            Alterações de status:
          </Text>
          <Box pl={5} pr={5}>
            <RequestStatusLog logs={request.change_status_logs} />
          </Box>
          <Text fontSize="lg" mb={4} pl={5} borderBottom="1px solid #eee" pb={2} mt={4} fontWeight="semibold">
            Observações:
          </Text>
          {permissions.editRequests && (
            <Box p={5} borderRadius="md" border="2px solid #ff8fbe" mx="auto" boxShadow="md">
              <Flex dir="row" alignItems="center" mb={4}>
                <Avatar src={user.picture} mr={3} size="md" />
                <Text fontWeight="semibold" fontSize="lg">
                  {user.name}
                </Text>
              </Flex>
              <Textarea
                border="none"
                borderRadius="none"
                placeholder="Digite aqui as observações da solicitação..."
                borderBottom="1px solid #eee"
                focusBorderColor="none"
                rows={4}
                onChange={(e) => setNote(e.target.value)}
                value={note}
              />
              <Flex dir="row" justifyContent="flex-end">
                <Button colorScheme="magenta" mt={4} onClick={handleSaveNote}>
                  Salvar
                </Button>
              </Flex>
            </Box>
          )}
          {notes.length === 0 && (
            <Text fontWeight="semibold" color="gray.400" mt={4} textAlign="center">
              Nenhuma observação registrada ainda...
            </Text>
          )}

          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            notes.map((_note: any) => (
              <Box p={5} borderRadius="md" mt={4} key={_note.id}>
                <Flex dir="row" alignItems="center" mb={4}>
                  <Avatar src={_note.created_by_picture} mr={3} h={10} w={10} />
                  <Flex alignItems="baseline">
                    <Text fontWeight="semibold" fontSize="lg">
                      {_note.created_by}
                    </Text>
                    <Text variant="caption" ml={2}>
                      {formatDateTime(_note.created_at, { showSeconds: true })}
                    </Text>
                  </Flex>
                  <Spacer />
                  {permissions.editRequests && (
                    <ActionButton action="delete" size="sm" onClick={() => handleDeleteNote(_note)} />
                  )}
                </Flex>
                <Text color="gray.500" mb={4}>
                  {_note.note}
                </Text>
                <Flex dir="row" justifyContent="flex-end">
                  <Text variant="caption">
                    {_note.updated_at !== _note.created_at && (
                      <Text variant="caption" as="span">
                        {` - Última edição em ${formatDateTime(_note.updated_at, { showSeconds: true })}`} por{' '}
                        <Avatar src={_note.updated_by_picture} w={5} h={5} /> {_note.updated_by}{' '}
                      </Text>
                    )}
                  </Text>
                </Flex>
              </Box>
            ))
          }
        </FormModal>
        <RequestDeleteDialog
          isOpen={deleteDialogIsOpen}
          onClose={deleteDialogIsClose}
          request={request}
          onDelete={onClose}
        />
        <RequestNoteDeleteDialog
          isOpen={deleteNoteDialogIsOpen}
          onClose={deleteNoteDialogIsClose}
          request={noteToDelete}
          onDelete={onCloseNoteDelete}
        />
      </>
    )
  )
}
